import { isEmpty, omit } from "lodash"
import React, { MouseEventHandler, useEffect, useState } from "react"
import { Button, Col, Form, Modal, Row } from "react-bootstrap"
import ReactFlagsSelect from "react-flags-select"
import { useTranslation } from "react-i18next"
import "react-phone-input-2/lib/style.css"

const AddWarehouseModal = ({
    show,
    loading,
    handleClose,
    handleSubmit,
    warehouseDetails,
    setWarehouseDetails,
    errors,
    setErrors
}: {
    show: boolean
    loading: boolean
    handleClose: () => void
    handleSubmit: Function
    warehouseDetails: Record<string, any>
    setWarehouseDetails: Function
    errors: Record<string, any>
    setErrors: Function
}) => {
    const { t, i18n } = useTranslation()
    const classNameForPhoneInput = `${i18n.language === "ar" ? "ltr-direction w-100" : "w-100"} ${errors?.phone ? "is-invalid" : ""}`
    const [isDisabled, setIsDisabled] = useState(false)
    const checkFields = () => {
        if (
            !loading &&
            warehouseDetails.name &&
            warehouseDetails.name !== "" &&
            warehouseDetails.address &&
            warehouseDetails.address !== "" &&
            warehouseDetails.city &&
            warehouseDetails.city !== "" &&
            warehouseDetails.phone &&
            warehouseDetails.phone !== "" &&
            isEmpty(errors)
        ) {
            return setIsDisabled(false)
        } else {
            return setIsDisabled(true)
        }
    }

    const handlePhoneInput = (phoneValue: string) => {
        setWarehouseDetails({ ...warehouseDetails, phone: phoneValue })
        if (!!errors["phone"]) setErrors(omit(errors, ["phone", "genericError"]))
    }

    useEffect(() => {
        checkFields()
    }, [
        loading,
        warehouseDetails.name,
        warehouseDetails.address,
        warehouseDetails.country,
        warehouseDetails.city,
        warehouseDetails.phone,
        isEmpty(errors)
    ])

    return (
        <Modal show={show} onHide={handleClose} backdrop="static" keyboard={false} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>{t("Add New Warehouse")}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form>
                    {errors["genericError"] && (
                        <div className="alert alert-danger">{errors["genericError"]}</div>
                    )}
                    <Row>
                        <Col lg={6}>
                            <Form.Group className="mb-3">
                                <Form.Label htmlFor="warehouseName">{t("Warehouse Name")}</Form.Label>
                                <Form.Control
                                    id="warehouseName"
                                    name={"name"}
                                    value={warehouseDetails.name}
                                    onChange={(e: any) => {
                                        setWarehouseDetails({
                                            ...warehouseDetails,
                                            [e.target.name]: e.target.value
                                        })
                                        if (!!errors[e.target.name])
                                            setErrors(omit(errors, [e.target.name, "genericError"]))
                                    }}
                                    placeholder="Warehouse name..."
                                    isInvalid={!!errors?.name}
                                />
                                <Form.Control.Feedback type="invalid">
                                    {t(`${errors.name}`)}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                        <Col lg={6}>
                            <Form.Group className="mb-3">
                                <Form.Label htmlFor="warehouseCountry">{t("Warehouse Country")}</Form.Label>
                                <ReactFlagsSelect
                                    id="warehouseCountry"
                                    // countries={countries?.map(country => country.iso2_code)}
                                    // className={`${i18n.language === "ar" && "rtl-direction"}`}
                                    selected={warehouseDetails.country || "AE"}
                                    onSelect={(code) => {
                                        setWarehouseDetails({ ...warehouseDetails, country: code })
                                        if (!!errors["country"])
                                            setErrors(omit(errors, ["country", "genericError"]))
                                    }}
                                    showSelectedLabel
                                    className={errors?.country ? "is-invalid" : ""}
                                    placeholder={"Select a country"}
                                    alignOptionsToRight
                                    fullWidth
                                />
                                <Form.Control.Feedback type="invalid">
                                    {t(`${errors.country}`)}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row>
                        <Col lg={4}>
                            <Form.Group className="mb-3">
                                <Form.Label htmlFor="warehousePhone">{t("Warehouse Phone")}</Form.Label>
                                <Form.Control
                                    id="warehousePhone"
                                    name={"phone"}
                                    value={warehouseDetails.phone}
                                    onChange={(e: any) => {
                                        setWarehouseDetails({
                                            ...warehouseDetails,
                                            [e.target.name]: e.target.value
                                        })
                                        if (!!errors[e.target.name])
                                            setErrors(omit(errors, [e.target.name, "genericError"]))
                                    }}
                                    placeholder="Warehouse phone no..."
                                    isInvalid={!!errors?.phone}
                                />
                                <Form.Control.Feedback type="invalid">
                                    {t(`${errors.phone}`)}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                        <Col lg={4}>
                            <Form.Group className="mb-3">
                                <Form.Label htmlFor="warehouseCity">{t("Warehouse City")}</Form.Label>
                                <Form.Control
                                    id="warehouseCity"
                                    name={"city"}
                                    value={warehouseDetails.city}
                                    onChange={(e: any) => {
                                        setWarehouseDetails({
                                            ...warehouseDetails,
                                            [e.target.name]: e.target.value
                                        })
                                        if (!!errors[e.target.name])
                                            setErrors(omit(errors, [e.target.name, "genericError"]))
                                    }}
                                    placeholder="Warehouse city..."
                                    isInvalid={!!errors?.city}
                                />
                                <Form.Control.Feedback type="invalid">
                                    {t(`${errors.city}`)}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                        <Col lg={4}>
                            <Form.Group className="mb-3">
                                <Form.Label htmlFor="warehouseState">{t("Warehouse State")}</Form.Label>
                                <Form.Control
                                    id="warehouseState"
                                    name={"state"}
                                    value={warehouseDetails.state}
                                    onChange={(e: any) => {
                                        setWarehouseDetails({
                                            ...warehouseDetails,
                                            [e.target.name]: e.target.value
                                        })
                                        if (!!errors[e.target.name])
                                            setErrors(omit(errors, [e.target.name, "genericError"]))
                                    }}
                                    placeholder="Warehouse state..."
                                    isInvalid={!!errors?.state}
                                />
                                <Form.Control.Feedback type="invalid">
                                    {t(`${errors.state}`)}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row className="d-flex justify-content-between align-items-center">
                        <Col lg={4}>
                            <Form.Group className="mb-3">
                                <Form.Label htmlFor="warehouseDimUnit">{t("Warehouse dims unit")}</Form.Label>
                                <Form.Control
                                    id="warehouseDimUnit"
                                    name={"dim_unit"}
                                    value={warehouseDetails.dim_unit}
                                    onChange={(e: any) => {
                                        setWarehouseDetails({
                                            ...warehouseDetails,
                                            [e.target.name]: e.target.value
                                        })
                                        if (!!errors[e.target.name])
                                            setErrors(omit(errors, [e.target.name, "genericError"]))
                                    }}
                                    placeholder="Warehouse Dimensions unit..."
                                    isInvalid={!!errors?.dim_unit}
                                />
                                <Form.Control.Feedback type="invalid">
                                    {t(`${errors.dim_unit}`)}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                        <Col lg={4}>
                            <Form.Group className="mb-3">
                                <Form.Label htmlFor="warehouseWeightUnit">
                                    {t("Warehouse weight unit")}
                                </Form.Label>
                                <Form.Control
                                    id="warehouseWeightUnit"
                                    name={"weight_unit"}
                                    value={warehouseDetails.weight_unit}
                                    onChange={(e: any) => {
                                        setWarehouseDetails({
                                            ...warehouseDetails,
                                            [e.target.name]: e.target.value
                                        })
                                        if (!!errors[e.target.name])
                                            setErrors(omit(errors, [e.target.name, "genericError"]))
                                    }}
                                    placeholder="Warehouse Weight unit..."
                                    isInvalid={!!errors?.weight_unit}
                                />
                                <Form.Control.Feedback type="invalid">
                                    {t(`${errors.weight_unit}`)}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                        <Col lg={4}>
                            <Form.Group className="mb-3">
                                <Form.Label htmlFor="warehouseCurrency">{t("Warehouse Currency")}</Form.Label>
                                <Form.Control
                                    id="warehouseCurrency"
                                    name={"currency"}
                                    value={warehouseDetails.currency}
                                    onChange={(e: any) => {
                                        setWarehouseDetails({
                                            ...warehouseDetails,
                                            [e.target.name]: e.target.value
                                        })
                                        if (!!errors[e.target.name])
                                            setErrors(omit(errors, [e.target.name, "genericError"]))
                                    }}
                                    placeholder="Warehouse Currency..."
                                    isInvalid={!!errors?.currency}
                                />
                                <Form.Control.Feedback type="invalid">
                                    {t(`${errors.currency}`)}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row className="d-flex justify-content-between align-items-center">
                        <Col lg={5}>
                            <Form.Group>
                                <Form.Label htmlFor="warehouseAddress">
                                    {t("Warehouse Address line 1")}
                                </Form.Label>
                                <Form.Control
                                    id="warehouseAddress"
                                    name={"address"}
                                    value={warehouseDetails.address}
                                    onChange={(e: any) => {
                                        setWarehouseDetails({
                                            ...warehouseDetails,
                                            [e.target.name]: e.target.value
                                        })
                                        if (!!errors[e.target.name])
                                            setErrors(omit(errors, [e.target.name, "genericError"]))
                                    }}
                                    placeholder="Warehouse address..."
                                    isInvalid={!!errors?.address}
                                />
                                <Form.Control.Feedback type="invalid">
                                    {t(`${errors.address}`)}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                        <Col lg={4}>
                            <Form.Group>
                                <Form.Label htmlFor="warehouseAddress2">
                                    {t("Warehouse Address line 2")}
                                </Form.Label>
                                <Form.Control
                                    id="warehouseAddress2"
                                    name={"address2"}
                                    value={warehouseDetails.address2}
                                    onChange={(e: any) => {
                                        setWarehouseDetails({
                                            ...warehouseDetails,
                                            [e.target.name]: e.target.value
                                        })
                                        if (!!errors[e.target.name])
                                            setErrors(omit(errors, [e.target.name, "genericError"]))
                                    }}
                                    placeholder="Warehouse address..."
                                    isInvalid={!!errors?.address2}
                                />
                                <Form.Control.Feedback type="invalid">
                                    {t(`${errors.address2}`)}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                        <Col lg={3}>
                            <Form.Group>
                                <Form.Label htmlFor="warehouseZipCode">{t("Warehouse Zip code")}</Form.Label>
                                <Form.Control
                                    id="warehouseZipCode"
                                    name={"zip_code"}
                                    value={warehouseDetails.zip_code}
                                    onChange={(e: any) => {
                                        setWarehouseDetails({
                                            ...warehouseDetails,
                                            [e.target.name]: e.target.value
                                        })
                                        if (!!errors[e.target.name])
                                            setErrors(omit(errors, [e.target.name, "genericError"]))
                                    }}
                                    placeholder="Warehouse zip code..."
                                    isInvalid={!!errors?.zip_code}
                                />
                                <Form.Control.Feedback type="invalid">
                                    {t(`${errors.zip_code}`)}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                        {/* <Col lg={3} className="d-none">
							<Form.Group className="mt-4">
								<Form.Check
									type="checkbox"
									name="active"
									label={t("Activate")}
									onChange={(e: any) => 
										setWarehouseDetails({ ...warehouseDetails, [e.target.name]: e.target.checked ? 1 : 0 })
									}
									required={true}
									checked={warehouseDetails.active}
									defaultChecked={true}
								/>
							</Form.Group>
						</Col> */}
                    </Row>
                </Form>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose as MouseEventHandler<HTMLButtonElement>}>
                    {t("Close")}
                </Button>

                <Button
                    disabled={isDisabled}
                    onClick={() => handleSubmit(warehouseDetails)}
                    className="btn btn-primary"
                    type="submit">
                    {loading ? (
                        <div className="spinner-border text-light" role="status"></div>
                    ) : warehouseDetails?.id ? (
                        t("Update")
                    ) : (
                        t("Submit")
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    )
}

export default AddWarehouseModal
